// Shared constants across the application

export const CHANGE_TYPE_LABELS = {
  new_starter: 'New Starter',
  leaver: 'Leaver',
  salary: 'Salary Change',
  pension: 'Pension Change',
  parental_leave: 'Parental Leave',
  sick_leave: 'Sick Leave',
  addition: 'Addition',
  deduction: 'Deduction',
  bank_details: 'Bank Details',
  address_change: 'Address Change',
  contractual_change: 'Contractual Change',
  personal_details: 'Personal Details',
};

export const CHANGE_TYPE_COLORS = {
  new_starter: 'text-green-700 bg-green-50',
  leaver: 'text-red-700 bg-red-50',
  salary: 'text-blue-700 bg-blue-50',
  pension: 'text-purple-700 bg-purple-50',
  parental_leave: 'text-pink-700 bg-pink-50',
  sick_leave: 'text-orange-700 bg-orange-50',
  addition: 'text-emerald-700 bg-emerald-50',
  deduction: 'text-amber-700 bg-amber-50',
  bank_details: 'text-cyan-700 bg-cyan-50',
  address_change: 'text-teal-700 bg-teal-50',
  contractual_change: 'text-indigo-700 bg-indigo-50',
  personal_details: 'text-teal-700 bg-teal-50',
};

// One line for a personal_details change: "DOB 05/06/1998 → 05/06/1999 · NI number updated".
// change_data carries new_<field> for what changed and previous_<field> for the old
// value (never for the NI number, which is kept out of change_data).
export function personalDetailsSummary(d = {}) {
  const ukDate = (v) => {
    const m = String(v || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : (v || '');
  };
  const parts = [];
  if (d.new_first_name || d.new_last_name) {
    const before = [d.previous_first_name, d.previous_last_name].filter(Boolean).join(' ');
    const after = [d.new_first_name || d.previous_first_name, d.new_last_name || d.previous_last_name].filter(Boolean).join(' ');
    parts.push(before ? `Name ${before} → ${after}` : `Name ${after}`);
  }
  if (d.new_date_of_birth) {
    parts.push(d.previous_date_of_birth
      ? `DOB ${ukDate(d.previous_date_of_birth)} → ${ukDate(d.new_date_of_birth)}`
      : `DOB ${ukDate(d.new_date_of_birth)}`);
  }
  if (d.new_gender) parts.push(`Gender ${d.previous_gender ? `${d.previous_gender} → ` : ''}${d.new_gender}`);
  if (d.new_ni_number) parts.push('NI number updated');
  const summary = parts.join(' · ') || 'Personal details updated';
  return d.reason ? `${summary} (${d.reason})` : summary;
}

export const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export const TAX_MONTH_NAMES = [
  'April', 'May', 'June', 'July', 'August', 'September',
  'October', 'November', 'December', 'January', 'February', 'March',
];

export function taxMonthToName(taxMonth) {
  return TAX_MONTH_NAMES[(taxMonth - 1) % 12] || `Month ${taxMonth}`;
}

export const ROLES = {
  ADMIN: 'admin',
  STANDARD: 'standard',
  COMPANY_ADMIN: 'company_admin',
  USER: 'user',
};

// UK Bank Holiday calculation (Anonymous Gregorian algorithm for Easter)
export function getEasterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

export function getUKBankHolidays(year) {
  const holidays = [];
  let nyd = new Date(year, 0, 1);
  if (nyd.getDay() === 0) nyd = new Date(year, 0, 2);
  if (nyd.getDay() === 6) nyd = new Date(year, 0, 3);
  holidays.push(nyd.getTime());
  const easter = getEasterSunday(year);
  const goodFriday = new Date(easter); goodFriday.setDate(easter.getDate() - 2);
  const easterMonday = new Date(easter); easterMonday.setDate(easter.getDate() + 1);
  holidays.push(goodFriday.getTime());
  holidays.push(easterMonday.getTime());
  const may1 = new Date(year, 4, 1);
  const earlyMay = new Date(year, 4, 1 + (8 - may1.getDay()) % 7);
  holidays.push(earlyMay.getTime());
  const mayEnd = new Date(year, 5, 0);
  const springBH = new Date(year, 4, mayEnd.getDate() - (mayEnd.getDay() + 6) % 7);
  holidays.push(springBH.getTime());
  const augEnd = new Date(year, 8, 0);
  const summerBH = new Date(year, 7, augEnd.getDate() - (augEnd.getDay() + 6) % 7);
  holidays.push(summerBH.getTime());
  let xmas = new Date(year, 11, 25);
  if (xmas.getDay() === 0) xmas = new Date(year, 11, 27);
  if (xmas.getDay() === 6) xmas = new Date(year, 11, 27);
  holidays.push(xmas.getTime());
  let boxing = new Date(year, 11, 26);
  if (boxing.getDay() === 0) boxing = new Date(year, 11, 28);
  if (boxing.getDay() === 6) boxing = new Date(year, 11, 28);
  holidays.push(boxing.getTime());
  return holidays;
}

export function isUKBankHoliday(date) {
  const holidays = getUKBankHolidays(date.getFullYear());
  return holidays.includes(new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime());
}

// Admin run-sheet adjustment types (must mirror ADMIN_ADJUSTMENT_TYPES in
// backend/src/utils/adjustmentCategories.js — the backend fixes the tax bucket
// from the `type`). 'other' lets the processor pick the treatment.
export const ADMIN_ADJUSTMENT_TYPES = [
  { value: 'pilon', label: 'PILON (pay in lieu of notice)' },
  { value: 'enhanced_parental_topup', label: 'Enhanced parental-leave top-up' },
  { value: 'holiday_pay', label: 'Holiday pay on leaving' },
  { value: 'adhoc_bonus', label: 'Ad-hoc bonus / commission' },
  { value: 'arrears', label: 'Backdated pay / arrears' },
  { value: 'redundancy_exgratia', label: 'Redundancy / ex-gratia' },
  { value: 'expense_reimbursement', label: 'Expense reimbursement' },
  { value: 'salary_sacrifice', label: 'Salary-sacrifice deduction' },
  { value: 'net_deduction', label: 'Net deduction' },
  { value: 'other', label: 'Other' },
];

// Tax treatment options shown only when type === 'other'.
export const ADMIN_ADJUSTMENT_TREATMENTS = [
  { value: 'gross_add', label: 'Taxable addition' },
  { value: 'non_taxable_add', label: 'Non-taxable addition' },
  { value: 'pre_tax_deduct', label: 'Pre-tax deduction (reduces taxable pay)' },
  { value: 'post_tax_deduct', label: 'Net deduction (post-tax)' },
];

// How a director's NI is worked out — set on the new starter form, keyed into BrightPay.
export const DIRECTOR_NI_LABELS = {
  alternative: 'Alternative method (pay-period basis)',
  annual: 'Annual method',
  unsure: 'Not sure — client left it to us',
};
